import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate, useParams, Navigate } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import {
    Activity,
    ArrowRight,
    BarChart3,
    Bot,
    BrainCircuit,
    ChevronRight,
    Compass,
    Database,
    Fingerprint,
    Gauge,
    Globe,
    LineChart,
    Mail,
    Map as MapIcon,
    Menu,
    Radar,
    Rocket,
    ShieldCheck,
    Sparkles,
    TrendingUp,
    Users,
    Workflow,
    X,
    Zap,
} from "lucide-react";

import { BLOG } from "./blog";

const LOGO = "/assets/logo/pdtech.png";

/* ======================================================
   TRANSLATIONS
   ====================================================== */
const T = {
    tr: {
        nav: {
            ozellikler: "Özellikler",
            moduller: "Modüller",
            teknoloji: "Teknoloji",
            blog: "Blog",
            iletisim: "İletişim",
            cta: "Demo Talep Et",
        },
        hero: {
            badge: "AI-POWERED FINTECH PLATFORM • 2026",
            title: ["Finansınızı", "gerçek zamanlı", "zekaya", "dönüştürün."],
            sub: "Virtual CFO, bayi performansından kredi riskine, fiyat elastikiyetinden finansal istihbarata kadar CFO ofisinin tüm operasyonunu tek AI platformunda birleştirir.",
            cta1: "Canlı Demo Talep Et",
            cta2: "Modülleri Keşfet",
            metrics: [
                { v: "500+", l: "Bayi / İstasyon" },
                { v: "8 ML", l: "Modeli" },
                { v: "<1 sn", l: "Yanıt Süresi" },
                { v: "%75", l: "Zaman Tasarrufu" },
            ],
            ticker: [
                "Bayi Performansı", "Fiyat Elastikiyeti", "Müşteri Davranışı",
                "Kredi & Risk", "Finansal İstihbarat", "LLM Storytelling",
                "Risk Skoru <5 sn", "MongoDB Persistent", "576 Simülasyon", "8 ML Modeli",
            ],
        },
        why: {
            kicker: "Neden Virtual CFO?",
            title: ["Geleneksel yaklaşım,", "modern CFO'ya", "yetmiyor."],
            desc: "Dağınık veri, sabit formüller, haftalık raporlar. Virtual CFO bu dört darboğazı tek AI platformuyla ortadan kaldırır.",
            probsHead: "GELENEKSEL YAKLAŞIMIN ZAYIFLIKLARI",
            probContext: "geleneksel sistemlerde",
            probs: [
                { n: "%60", t: "Zaman Kaybı", d: "Ekiplerin yarısından fazlası manuel konsolidasyonla geçiyor." },
                { n: "3-4 hf", t: "Veri Gecikmesi", d: "Aylık kapanışlar güncel olmayan veriyle karar verdiriyor." },
                { n: "5+", t: "Veri Silosu", d: "ERP, Excel ve sistemler arası birleşik görünüm yok." },
                { n: "%0", t: "AI Kullanımı", d: "Statik modeller dalgalanmayı ve anomaliyi yakalayamıyor." },
            ],
            solKicker: "VIRTUAL CFO ÇÖZÜMÜ",
            solTitle: ["Tek platform.", "Dört darboğaz için yapay zeka."],
            sols: [
                "10x daha hızlı raporlama döngüsü",
                "%40 ekip zaman tasarrufu",
                "27+ değişkenle dinamik risk motoru",
                "Gerçek zamanlı anomali ve churn erken uyarısı",
                "LLM ile doğal dilde karar desteği",
                "Kurum verisine güvenli entegrasyon",
            ],
        },
        modules: {
            kicker: "Platform Modülleri",
            title: ["Tek platform,", "beş akıllı", "modül."],
            desc: "Finans, satış ve operasyon ekipleri için entegre, gerçek zamanlı karar motoru.",
            moduleLabel: "Modül",
            livePreview: "Canlı Veri Önizleme",
            chartTitle: "Performans Trendi",
            chartActual: "Gerçekleşen",
            chartTarget: "Hedef",
            months: ["Oca","Şub","Mar","Nis","May","Haz","Tem","Ağu","Eyl","Eki","Kas","Ara"],
            statusLabels: { uptime: "Uptime", security: "Güvenlik", response: "Yanıt" },
            items: [
                {
                    id: "bayi", num: "01", name: "Bayi Performansı",
                    title: "Bayi Performans Yönetimi",
                    tagline: "500 bayiyi tek ekranda izleyen AI destekli akıllı analiz platformu.",
                    kpis: [{ v:"612", l:"Bayi" },{ v:"-4.8%", l:"Bütçe Sapması" },{ v:"8", l:"ML Modeli" }],
                    bullets: [
                        "Gerçek zamanlı KPI dashboard & aktif alarmlar",
                        "K-Means, Random Forest, Isolation Forest, XGBoost…",
                        "Risk skoru 0-100 ve churn olasılığı tahmini",
                        "Coğrafi kümeleme & potansiyel skor (nüfus + gelir + araç)",
                        "Gold / Silver / Bronze kategorileri ve 7 bölge analizi",
                        "Türkiye haritası: 612 nokta renk kodlu görünüm",
                    ],
                },
                {
                    id: "ticari", num: "02", name: "Ticari / Fiyat",
                    title: "AI Destekli İndirim Karar Motoru",
                    tagline: "Whitepaper %100 uyumlu fiyat elastikiyeti & senaryo simülasyonu.",
                    kpis: [{ v:"35+", l:"Aktif Müşteri" },{ v:"<1 sn", l:"Yanıt" },{ v:"3-5x", l:"Hedef ROI" }],
                    bullets: [
                        "Kredili müşteriler 3× daha fiyat duyarlı (0.24 vs 0.08)",
                        "Asimetrik indirim: artış +0.21, düşüş -0.02",
                        "4 strateji: Baseline, Volume Growth, Margin, Balanced",
                        "DSL tabanlı açıklanabilir kural motoru",
                        "LLM ile bağlamsal Q&A + satış senaryosu üretimi",
                        "Tahmini yıllık ek EBITDA: 25-40M ₺",
                    ],
                },
                {
                    id: "musteri", num: "03", name: "Müşteri Davranışı",
                    title: "Müşteri Davranış Yönlendirme Sistemi",
                    tagline: "Ham işlem verisini aksiyon alınabilir sadakat içgörüsüne çevirir.",
                    kpis: [{ v:"125+", l:"İstasyon" },{ v:"600+", l:"Müşteri" },{ v:"576", l:"Senaryo" }],
                    bullets: [
                        "5 davranış segmenti: Morning Routine, High-Volume Cash…",
                        "XGBoost (%74.4), GBM (%73.8), RF (%72.1) paralel ML",
                        "Türkiye ısı haritası • Bölge → İl → İstasyon drill-down",
                        "What-If Simulator: 576 kombinasyon grid search",
                        "Plate 360 — plaka bazlı profil + GPT doğal dil sorgusu",
                        "Kampanya ROI ölçümü ve erken churn uyarısı",
                    ],
                },
                {
                    id: "risk", num: "04", name: "Kredi & Risk",
                    title: "Kredi & Risk Yönetimi",
                    tagline: "Dinamik risk metodolojisi ve 360° portföy görünümü.",
                    kpis: [{ v:"27+", l:"Değişken" },{ v:"<5 sn", l:"Risk Skor" },{ v:"%90", l:"Otomasyon" }],
                    bullets: [
                        "Akıllı doküman işleme: Excel/PDF, VKN tanıma",
                        "Dinamik formüller — özelleştirilebilir ağırlık & eşik",
                        "A-B-C-D-E risk bandı & teminat oranı izleme",
                        "Dönem karşılaştırma + sektör benchmark",
                        "LLM trend analizi ve 3-5 aksiyon önerisi",
                        "Devreye alma 6-12 ay yerine birkaç hafta",
                    ],
                },
                {
                    id: "finansal", num: "05", name: "Finansal İstihbarat",
                    title: "Finansal İstihbarat & AI Analytics Hub",
                    tagline: "395K+ EPM kaydı üzerinde LLM destekli, yönetim-kuruluna-hazır analiz.",
                    kpis: [{ v:"395K+", l:"EPM Kayıt" },{ v:"12 ay", l:"Forecast" },{ v:"10x", l:"Hızlanma" }],
                    bullets: [
                        "Variance Analysis — plan vs. actual kök neden",
                        "Scenario Planning — best/worst/optimistic AI senaryoları",
                        "Rolling Forecast — 12 aylık Bayesian projeksiyon",
                        "Fraud Detection — tüm işlemlerde anomali skorlama",
                        "Strategic Optimizer — confidence skorlu AI önerileri",
                        "AI Storytelling: ham sayılar → executive-ready anlatı",
                    ],
                },
            ],
        },
        features: {
            kicker: "Güçlü Özellikler",
            title: ["Daha fazlasını üretin,", "daha az yönetin."],
            desc: "Virtual CFO'nun öne çıkan modül yetenekleri — hepsi AI tarafından desteklenir, tek API ile erişilir.",
            items: [
                { title: "LLM AI Asistan", desc: "Doğal dilde finansal soru-cevap, anlık öneri ve senaryo üretimi. Bağlamsal bellek ile tutarlı kararlar." },
                { title: "DSL Kural Motoru", desc: "Açıklanabilir kurallar: BLOCK, HOLD, RECOMMEND_DISCOUNT_CHANGE. Margin floor ve MAP koruma otomatik." },
                { title: "Dinamik Risk Motoru", desc: "27+ değişken üzerine özelleştirilebilir formüller, ağırlıklar ve eşikler. Canlı önizleme." },
                { title: "Anomali Tespiti", desc: "Isolation Forest ve istatistiksel yöntemlerle %100 işlem kapsamı. Kritik/High/Medium/Low skoru." },
                { title: "Rolling Forecast", desc: "12 aylık Bayesian projeksiyon. Mevsimsellik, iş döngüsü ve tarihsel trendleri birlikte modeller." },
                { title: "AI Storytelling", desc: "Ham analiz çıktılarını kök-neden + aksiyon önerili executive anlatıya çevirir. Tek tıkla sunuma hazır." },
            ],
        },
        arch: {
            kicker: "Mimari",
            title: ["Uçtan uca,", "kurumsal düzeyde", "mimari."],
            desc: "Mevcut sistemlerinizi bozmadan üzerine entegre olan; React, FastAPI, MongoDB ve büyük dil modeli (LLM) üzerinde çalışan ölçeklenebilir mimari.",
            cols: [
                { title: "Veri Kaynakları", items: ["ERP / CRM", "Saha / POS Sistemleri", "Excel / PDF Bilançolar", "API & Bulut"] },
                { title: "Virtual CFO Core", items: ["ETL & Senkronizasyon", "Güvenlik & Yönetişim", "AI Çekirdeği (LLM)", "MongoDB Persistent"] },
                { title: "Uygulamalar", items: ["Dashboardlar", "What-If Lab", "AI Storytelling", "BI & Özel Raporlar"] },
            ],
            stackLabel: "Teknoloji Yığını",
        },
        stats: [
            { v: "10x", l: "Hızlı Raporlama", d: "Haftalardan saniyelere" },
            { v: "%75", l: "Zaman Tasarrufu", d: "Manuel analiz → otomasyon" },
            { v: "%95", l: "Hedef Tutturma", d: "ML destekli gerçekçi hedef" },
            { v: "6 ay", l: "Geri Dönüş", d: "Tipik ROI süresi" },
        ],
        roadmap: {
            kicker: "Hizmet Modeli",
            title: ["Başlangıçtan", "ölçeklendirmeye."],
            phases: [
                { t: "Keşif", d: "Mevcut sistem analizi, veri kaynaklarının haritalanması.", dur: "1-2 hafta" },
                { t: "Pilot", d: "Seçili modül ve bölgede canlı ortamda doğrulama.", dur: "3-4 hafta" },
                { t: "Entegrasyon", d: "ERP ve saha sistemleri ile tam entegrasyon.", dur: "4-6 hafta" },
                { t: "Ölçeklendirme", d: "Tüm ağ, ek modüller, eğitim ve destek.", dur: "Sürekli" },
            ],
            note: "Tipik geri dönüş süresi 6 ay — mevcut sistemleri değiştirmeden, üzerine entegre.",
        },
        contact: {
            badge: "Hazır olduğunuzda başlayalım",
            title: ["Veri dönüşümü yolculuğunuza", "bugün", "başlayın."],
            desc: "Virtual CFO'nun kurumunuza nasıl değer katabileceğini keşfetmek için bizimle iletişime geçin. Size özel canlı demo planlayalım.",
            cta2: "Modülleri İncele",
            actions: [
                { t: "Demo Talebi", d: "Sistemi canlı deneyimleyin" },
                { t: "Pilot Uygulama", d: "Seçili bölgede başlayın" },
                { t: "Entegrasyon", d: "Mevcut sistemlerinize bağlayın" },
            ],
        },
        footer: {
            tagline: "Virtual CFO — yapay zeka destekli finansal zeka platformu. Verilerinizi gerçek zamanlı karar desteğine dönüştürüyoruz.",
            platform: "Platform",
            contact: "İletişim",
            company: "PDTECH YAZILIM VE DANIŞMANLIK HİZMETLERİ A.Ş.",
            rights: "Tüm hakları saklıdır.",
        },
    },
    en: {
        nav: {
            ozellikler: "Features",
            moduller: "Modules",
            teknoloji: "Technology",
            blog: "Insights",
            iletisim: "Contact",
            cta: "Request Demo",
        },
        hero: {
            badge: "AI-POWERED FINTECH PLATFORM • 2026",
            title: ["Turn your finance", "into real-time", "intelligence,", "today."],
            sub: "Virtual CFO unifies dealer performance, credit risk, price elasticity and financial intelligence into a single AI platform for the entire CFO office.",
            cta1: "Request a Live Demo",
            cta2: "Explore Modules",
            metrics: [
                { v: "500+", l: "Dealers / Stations" },
                { v: "8 ML", l: "Models" },
                { v: "<1 s", l: "Response Time" },
                { v: "75%", l: "Time Saved" },
            ],
            ticker: [
                "Dealer Performance", "Price Elasticity", "Customer Behavior",
                "Credit & Risk", "Financial Intelligence", "LLM Storytelling",
                "Risk Score <5s", "MongoDB Persistent", "576 Simulations", "8 ML Models",
            ],
        },
        why: {
            kicker: "Why Virtual CFO?",
            title: ["Traditional tools", "can't keep up with", "the modern CFO."],
            desc: "Scattered data, rigid formulas, weekly reports. Virtual CFO eliminates all four bottlenecks in a single AI platform.",
            probsHead: "LIMITATIONS OF LEGACY APPROACHES",
            probContext: "in legacy systems",
            probs: [
                { n: "60%", t: "Time Wasted", d: "Finance teams spend over half their time on manual consolidation." },
                { n: "3-4 wk", t: "Data Lag", d: "Monthly closes force decisions on weeks-old data." },
                { n: "5+", t: "Data Silos", d: "No unified view across ERPs, spreadsheets and systems." },
                { n: "0%", t: "AI Usage", d: "Static models miss volatility, anomalies and emerging trends." },
            ],
            solKicker: "THE VIRTUAL CFO SOLUTION",
            solTitle: ["One platform.", "AI for all four bottlenecks."],
            sols: [
                "10x faster reporting cycles",
                "40% time saved across teams",
                "Dynamic risk engine with 27+ variables",
                "Real-time anomaly & churn early warning",
                "Natural-language decision support via LLM",
                "Secure integration with your enterprise data",
            ],
        },
        modules: {
            kicker: "Platform Modules",
            title: ["One platform,", "five intelligent", "modules."],
            desc: "An integrated, real-time decision engine for finance, sales and operations teams.",
            moduleLabel: "Module",
            livePreview: "Live Data Preview",
            chartTitle: "Performance Trend",
            chartActual: "Actual",
            chartTarget: "Target",
            months: ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"],
            statusLabels: { uptime: "Uptime", security: "Security", response: "Response" },
            items: [
                {
                    id: "bayi", num: "01", name: "Dealer Performance",
                    title: "Dealer Performance Management",
                    tagline: "AI-powered intelligent analytics platform monitoring 500 dealers on a single screen.",
                    kpis: [{ v:"612", l:"Dealers" },{ v:"-4.8%", l:"Budget Gap" },{ v:"8", l:"ML Models" }],
                    bullets: [
                        "Real-time KPI dashboard & active alerts",
                        "K-Means, Random Forest, Isolation Forest, XGBoost…",
                        "Risk score 0-100 & churn probability prediction",
                        "Geographic clustering & potential score (pop + income + vehicles)",
                        "Gold / Silver / Bronze categories and 7-region analysis",
                        "Turkey map: 612 locations, color-coded view",
                    ],
                },
                {
                    id: "ticari", num: "02", name: "Commercial / Price",
                    title: "AI-Powered Discount Decision Engine",
                    tagline: "100% whitepaper-compliant price elasticity & scenario simulation.",
                    kpis: [{ v:"35+", l:"Active Customers" },{ v:"<1 s", l:"Response" },{ v:"3-5x", l:"Target ROI" }],
                    bullets: [
                        "Credit customers 3× more price-sensitive (0.24 vs 0.08)",
                        "Asymmetric discount: +0.21 rise, -0.02 fall",
                        "4 strategies: Baseline, Volume Growth, Margin, Balanced",
                        "DSL-based explainable rule engine",
                        "Contextual Q&A + sales script generation with LLM",
                        "Estimated annual incremental EBITDA: 25-40M ₺",
                    ],
                },
                {
                    id: "musteri", num: "03", name: "Customer Behavior",
                    title: "Customer Behavior Routing System",
                    tagline: "Transforms raw transaction data into actionable loyalty insights.",
                    kpis: [{ v:"125+", l:"Stations" },{ v:"600+", l:"Customers" },{ v:"576", l:"Scenarios" }],
                    bullets: [
                        "5 behavioral segments: Morning Routine, High-Volume Cash…",
                        "XGBoost (74.4%), GBM (73.8%), RF (72.1%) parallel ML",
                        "Turkey heatmap • Region → Province → Station drill-down",
                        "What-If Simulator: 576 combination grid search",
                        "Plate 360 — license plate profile + GPT natural-language query",
                        "Campaign ROI measurement & early churn alerts",
                    ],
                },
                {
                    id: "risk", num: "04", name: "Credit & Risk",
                    title: "Credit & Risk Management",
                    tagline: "Dynamic risk methodology and 360° portfolio view.",
                    kpis: [{ v:"27+", l:"Variables" },{ v:"<5 s", l:"Risk Score" },{ v:"90%", l:"Automation" }],
                    bullets: [
                        "Smart document processing: Excel/PDF, Tax-ID recognition",
                        "Dynamic formulas — customizable weights & thresholds",
                        "A-B-C-D-E risk bands & collateral ratio tracking",
                        "Period comparison + sector benchmark",
                        "LLM trend analysis & 3-5 action recommendations",
                        "Deploy in weeks instead of 6-12 months",
                    ],
                },
                {
                    id: "finansal", num: "05", name: "Financial Intelligence",
                    title: "Financial Intelligence & AI Analytics Hub",
                    tagline: "Board-ready analysis on 395K+ EPM records, powered by LLM.",
                    kpis: [{ v:"395K+", l:"EPM Records" },{ v:"12 mo", l:"Forecast" },{ v:"10x", l:"Speed-up" }],
                    bullets: [
                        "Variance Analysis — plan vs. actual root-cause",
                        "Scenario Planning — best/worst/optimistic AI scenarios",
                        "Rolling Forecast — 12-month Bayesian projection",
                        "Fraud Detection — anomaly scoring across all transactions",
                        "Strategic Optimizer — AI recommendations with confidence scores",
                        "AI Storytelling: raw numbers → executive-ready narrative",
                    ],
                },
            ],
        },
        features: {
            kicker: "Powerful Capabilities",
            title: ["Build more,", "manage less."],
            desc: "Key capabilities across Virtual CFO modules — all AI-powered, all accessible via a single API.",
            items: [
                { title: "LLM AI Assistant", desc: "Natural-language financial Q&A, instant recommendations and scenario generation with contextual memory." },
                { title: "DSL Rule Engine", desc: "Explainable rules: BLOCK, HOLD, RECOMMEND_DISCOUNT_CHANGE. Automatic margin-floor and MAP protection." },
                { title: "Dynamic Risk Engine", desc: "Customizable formulas, weights and thresholds over 27+ variables. Live preview included." },
                { title: "Anomaly Detection", desc: "100% transaction coverage with Isolation Forest and statistical methods. Critical/High/Medium/Low severity." },
                { title: "Rolling Forecast", desc: "12-month Bayesian projection modeling seasonality, business cycles and historical trends jointly." },
                { title: "AI Storytelling", desc: "Turns raw analytical output into executive narrative with root cause + action recommendations. One-click board-ready." },
            ],
        },
        arch: {
            kicker: "Architecture",
            title: ["End-to-end,", "enterprise-grade", "architecture."],
            desc: "Integrates on top of your existing systems without disruption; a scalable architecture running on React, FastAPI, MongoDB and a large language model (LLM).",
            cols: [
                { title: "Data Sources", items: ["ERP / CRM", "Field / POS Systems", "Excel / PDF Statements", "API & Cloud"] },
                { title: "Virtual CFO Core", items: ["ETL & Sync", "Security & Governance", "AI Core (LLM)", "MongoDB Persistent"] },
                { title: "Applications", items: ["Dashboards", "What-If Lab", "AI Storytelling", "BI & Custom Reports"] },
            ],
            stackLabel: "Tech Stack",
        },
        stats: [
            { v: "10x", l: "Faster Reporting", d: "From weeks to seconds" },
            { v: "75%", l: "Time Saved", d: "Manual analysis → automation" },
            { v: "95%", l: "Target Hit Rate", d: "ML-backed realistic targets" },
            { v: "6 mo", l: "Payback", d: "Typical ROI timeframe" },
        ],
        roadmap: {
            kicker: "Service Model",
            title: ["From discovery", "to scale."],
            phases: [
                { t: "Discovery", d: "Current system analysis and data source mapping.", dur: "1-2 weeks" },
                { t: "Pilot", d: "Live validation on a selected module and region.", dur: "3-4 weeks" },
                { t: "Integration", d: "Full integration with ERP and field systems.", dur: "4-6 weeks" },
                { t: "Scale-up", d: "Full network, additional modules, training & support.", dur: "Ongoing" },
            ],
            note: "Typical payback in 6 months — integrates on top of existing systems without replacing them.",
        },
        contact: {
            badge: "Let's start when you're ready",
            title: ["Start your data", "transformation", "today."],
            desc: "Get in touch to see how Virtual CFO can create value for your organization. We'll plan a tailored live demo for you.",
            cta2: "Explore Modules",
            actions: [
                { t: "Demo Request", d: "Experience the system live" },
                { t: "Pilot Deployment", d: "Start in a selected region" },
                { t: "Integration", d: "Connect to your existing systems" },
            ],
        },
        footer: {
            tagline: "Virtual CFO — the AI-powered financial intelligence platform. We turn your data into real-time decision support.",
            platform: "Platform",
            contact: "Contact",
            company: "PDTECH YAZILIM VE DANIŞMANLIK HİZMETLERİ A.Ş.",
            rights: "All rights reserved.",
        },
    },
};

/* ======================================================
   LANGUAGE CONTEXT
   ====================================================== */
const LangCtx = createContext({ lang: "tr", t: T.tr, setLang: () => {} });
const useT = () => useContext(LangCtx);

const SITE_ORIGIN = "https://virtualcfo.com.tr";

function detectLang(pathname) {
    return pathname === "/en" || pathname.startsWith("/en/") ? "en" : "tr";
}

function LangProvider({ children }) {
    const location = useLocation();
    const navigate = useNavigate();
    const lang = detectLang(location.pathname);

    // Mirror current path into the other language
    const setLang = (nextLang) => {
        if (nextLang === lang) return;
        const path = location.pathname;
        let target;
        if (nextLang === "en") {
            // TR -> EN
            if (path === "/") target = "/en";
            else if (path.startsWith("/blog/")) {
                const slug = path.replace("/blog/", "");
                // swap slug if it's the known TR article
                const swapped = slug === BLOG.slug.tr ? BLOG.slug.en : slug;
                target = `/en/blog/${swapped}`;
            } else target = "/en";
        } else {
            // EN -> TR
            if (path === "/en") target = "/";
            else if (path.startsWith("/en/blog/")) {
                const slug = path.replace("/en/blog/", "");
                const swapped = slug === BLOG.slug.en ? BLOG.slug.tr : slug;
                target = `/blog/${swapped}`;
            } else target = "/";
        }
        try { localStorage.setItem("vcfo-lang", nextLang); } catch {}
        navigate(target + location.hash);
    };

    useEffect(() => {
        document.documentElement.lang = lang;
    }, [lang]);

    return (
        <LangCtx.Provider value={{ lang, t: T[lang], setLang }}>
            {children}
        </LangCtx.Provider>
    );
}

function LangToggle({ dark = true }) {
    const { lang, setLang } = useT();
    const toggle = () => setLang(lang === "tr" ? "en" : "tr");
    return (
        <button
            onClick={toggle}
            data-testid="lang-toggle"
            aria-label="Toggle language"
            className={`group inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold tracking-wider transition-colors ${
                dark
                    ? "bg-white/5 hover:bg-white/10 border border-white/15 text-white/80"
                    : "bg-[#0b0f1a]/5 hover:bg-[#0b0f1a]/10 border border-[#0b0f1a]/10 text-[#0b0f1a]/80"
            }`}
        >
            <Globe className="w-3.5 h-3.5" />
            <span className={lang === "tr" ? "text-[#f87171]" : "opacity-50"}>TR</span>
            <span className="opacity-30">/</span>
            <span className={lang === "en" ? "text-[#f87171]" : "opacity-50"}>EN</span>
        </button>
    );
}

/* ======================================================
   SEO HEAD
   ====================================================== */
function SeoHead({ title, description, path, trPath, enPath, type = "website", image }) {
    const { lang } = useT();
    const canonical = SITE_ORIGIN + path;
    const trUrl = SITE_ORIGIN + (trPath || "/");
    const enUrl = SITE_ORIGIN + (enPath || "/en");
    const ogLocale = lang === "en" ? "en_US" : "tr_TR";
    const ogLocaleAlt = lang === "en" ? "tr_TR" : "en_US";
    const img = image || `${SITE_ORIGIN}/assets/logo/pdtech.png`;
    return (
        <Helmet>
            <html lang={lang} />
            <title>{title}</title>
            <meta name="description" content={description} />
            <link rel="canonical" href={canonical} />
            <link rel="alternate" hrefLang="tr" href={trUrl} />
            <link rel="alternate" hrefLang="en" href={enUrl} />
            <link rel="alternate" hrefLang="x-default" href={trUrl} />
            <meta property="og:type" content={type} />
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:url" content={canonical} />
            <meta property="og:image" content={img} />
            <meta property="og:locale" content={ogLocale} />
            <meta property="og:locale:alternate" content={ogLocaleAlt} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={title} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={img} />
        </Helmet>
    );
}

/* ======================================================
   HOOKS
   ====================================================== */
function useReveal() {
    useEffect(() => {
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add("in");
                        io.unobserve(e.target);
                    }
                });
            },
            { threshold: 0.12 }
        );
        document.querySelectorAll(".reveal").forEach((el) => io.observe(el));
        return () => io.disconnect();
    }, []);
}

function useHideBadge() {
    useEffect(() => {
        const remove = () => {
            const el = document.getElementById("emergent-badge");
            if (el) el.remove();
            document
                .querySelectorAll('a[href*="emergent.sh"]')
                .forEach((a) => a.remove());
        };
        remove();
        const mo = new MutationObserver(remove);
        mo.observe(document.body, { childList: true, subtree: true });
        return () => mo.disconnect();
    }, []);
}

/* ======================================================
   NAVBAR
   ====================================================== */
function Navbar() {
    const { t, lang } = useT();
    const homePath = lang === "en" ? "/en" : "/";
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const NAV = [
        { id: "ozellikler", label: t.nav.ozellikler },
        { id: "moduller", label: t.nav.moduller },
        { id: "teknoloji", label: t.nav.teknoloji },
        { id: "blog", label: t.nav.blog },
        { id: "iletisim", label: t.nav.iletisim },
    ];

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <nav
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled ? "bg-[#0b0f1a]/85 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-5 md:px-8 h-[64px] sm:h-[68px] flex items-center justify-between gap-2">
                <Link to={homePath} data-testid="nav-logo" className="flex items-center gap-3 group">
                    <div className="h-9 bg-white rounded-md px-2 flex items-center">
                        <img src={LOGO} alt="PDTECH" className="h-5" />
                    </div>
                    <span className="hidden md:inline-block text-white/50 text-sm font-medium tracking-wide">
                        × Virtual CFO
                    </span>
                </Link>

                <div className="hidden md:flex items-center gap-8">
                    {NAV.map((n) => (
                        <a
                            key={n.id}
                            href={`${homePath === "/" ? "" : homePath}/#${n.id}`}
                            data-testid={`nav-${n.id}`}
                            className="link-underline text-white/80 hover:text-white text-sm font-medium"
                        >
                            {n.label}
                        </a>
                    ))}
                </div>

                <div className="flex items-center gap-3">
                    <LangToggle dark />
                    <a
                        href={`${homePath === "/" ? "" : homePath}/#iletisim`}
                        data-testid="nav-cta-demo"
                        className="hidden md:inline-flex items-center gap-2 bg-[#dc2626] hover:bg-[#b91c1c] text-white text-sm font-semibold px-4 py-2.5 rounded-full shine transition-colors"
                    >
                        {t.nav.cta}
                        <ArrowRight className="w-4 h-4" />
                    </a>
                    <button
                        onClick={() => setOpen((v) => !v)}
                        data-testid="nav-mobile-toggle"
                        className="md:hidden text-white p-2"
                        aria-label="Menu"
                    >
                        {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>
            {open && (
                <div className="md:hidden bg-[#0b0f1a]/95 backdrop-blur-xl border-t border-white/10 px-5 py-4 flex flex-col gap-4">
                    {NAV.map((n) => (
                        <a
                            key={n.id}
                            href={`${homePath === "/" ? "" : homePath}/#${n.id}`}
                            onClick={() => setOpen(false)}
                            data-testid={`nav-mobile-${n.id}`}
                            className="text-white/85 text-base py-2"
                        >
                            {n.label}
                        </a>
                    ))}
                    <a
                        href={`${homePath === "/" ? "" : homePath}/#iletisim`}
                        onClick={() => setOpen(false)}
                        data-testid="nav-mobile-cta"
                        className="bg-[#dc2626] text-white font-semibold px-4 py-3 rounded-full text-center"
                    >
                        {t.nav.cta}
                    </a>
                </div>
            )}
        </nav>
    );
}

/* ======================================================
   HERO
   ====================================================== */
function Hero() {
    const { t } = useT();
    const containerRef = useRef(null);
    const [mx, setMx] = useState({ x: 0, y: 0 });
    const onMove = (e) => {
        const r = containerRef.current?.getBoundingClientRect();
        if (!r) return;
        setMx({
            x: (e.clientX - r.left - r.width / 2) / r.width,
            y: (e.clientY - r.top - r.height / 2) / r.height,
        });
    };
    const [p1, p2, p3, p4] = t.hero.title;
    return (
        <section
            id="top"
            ref={containerRef}
            onMouseMove={onMove}
            data-testid="hero-section"
            className="relative min-h-screen bg-[#0b0f1a] overflow-hidden"
        >
            <div
                className="absolute -top-40 -right-40 w-[700px] h-[700px] rounded-full opacity-30 animate-blob-1 blur-3xl"
                style={{
                    background: "radial-gradient(circle at 30% 30%, #dc2626 0%, transparent 60%)",
                    transform: `translate(${mx.x * 30}px, ${mx.y * 30}px)`,
                }}
            />
            <div
                className="absolute -bottom-60 -left-40 w-[600px] h-[600px] rounded-full opacity-25 animate-blob-2 blur-3xl"
                style={{ background: "radial-gradient(circle at 70% 70%, #f59e0b 0%, transparent 55%)" }}
            />
            <div className="absolute inset-0 bg-grid-fade opacity-40" />
            <div className="noise-overlay" />

            <div className="relative max-w-7xl mx-auto px-5 md:px-8 hero-shrink-pt pt-36 pb-32 md:pb-24 min-h-screen flex flex-col justify-center">
                <div
                    className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/5 border border-white/10 text-white/80 text-[10px] sm:text-xs font-medium w-fit mb-6 sm:mb-8 reveal"
                    data-testid="hero-badge"
                >
                    <span className="w-2 h-2 rounded-full bg-[#dc2626] animate-pulse-ring" />
                    {t.hero.badge}
                </div>

                <h1
                    data-testid="hero-title"
                    className="reveal text-white text-[40px] sm:text-5xl md:text-7xl lg:text-[88px] font-extrabold leading-[1.05] tracking-tight text-balance max-w-5xl"
                >
                    {p1}{" "}
                    <span className="font-display italic font-normal text-white/80">{p2}</span>{" "}
                    <span className="text-gradient-red animate-gradient">{p3}</span>
                    {p4 ? <> {p4}</> : null}
                </h1>

                <p
                    data-testid="hero-sub"
                    className="reveal mt-6 sm:mt-8 max-w-2xl text-white/60 text-base sm:text-lg md:text-xl leading-relaxed"
                    style={{ transitionDelay: "120ms" }}
                >
                    {t.hero.sub}
                </p>

                <div className="reveal mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4" style={{ transitionDelay: "220ms" }}>
                    <a
                        href="#iletisim"
                        data-testid="hero-cta-primary"
                        className="group inline-flex items-center justify-center gap-2 bg-[#dc2626] hover:bg-[#b91c1c] text-white font-semibold px-6 sm:px-7 py-3.5 sm:py-4 rounded-full shine transition-colors text-sm sm:text-base"
                    >
                        {t.hero.cta1}
                        <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 transition-transform group-hover:translate-x-1" />
                    </a>
                    <a
                        href="#moduller"
                        data-testid="hero-cta-secondary"
                        className="group inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/15 text-white font-semibold px-6 sm:px-7 py-3.5 sm:py-4 rounded-full transition-colors text-sm sm:text-base"
                    >
                        {t.hero.cta2}
                        <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 transition-transform group-hover:translate-x-1" />
                    </a>
                </div>

                <div
                    className="reveal mt-10 sm:mt-16 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 max-w-4xl"
                    style={{ transitionDelay: "320ms" }}
                    data-testid="hero-metrics"
                >
                    {t.hero.metrics.map((m, i) => (
                        <div key={i} className="glass rounded-xl sm:rounded-2xl px-4 py-4 sm:px-5 sm:py-5 lift" data-testid={`hero-metric-${i}`}>
                            <div className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#f87171] font-numbers">{m.v}</div>
                            <div className="text-white/55 text-[10px] sm:text-xs md:text-sm mt-2 font-medium uppercase tracking-wider">
                                {m.l}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 border-t border-white/5 bg-[#0b0f1a]/60 backdrop-blur-sm overflow-hidden">
                <div className="flex gap-12 py-4 whitespace-nowrap animate-ticker">
                    {[...Array(2)].map((_, r) => (
                        <React.Fragment key={r}>
                            {t.hero.ticker.map((item, i) => (
                                <span
                                    key={`${r}-${i}`}
                                    className="text-white/40 text-sm font-medium tracking-wide flex items-center gap-3"
                                >
                                    <span className="w-1.5 h-1.5 rounded-full bg-[#dc2626]" />
                                    {item}
                                </span>
                            ))}
                        </React.Fragment>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ======================================================
   WHY
   ====================================================== */
function WhySection() {
    const { t } = useT();
    const { kicker, title, desc, probsHead, probContext, probs, solKicker, solTitle, sols } = t.why;
    return (
        <section id="ozellikler" data-testid="ozellikler-section" className="relative py-28 md:py-36 bg-[#f8fafc]">
            <div className="max-w-7xl mx-auto px-5 md:px-8">
                <div className="max-w-3xl reveal">
                    <div className="text-[#dc2626] text-sm font-bold tracking-widest uppercase mb-3">{kicker}</div>
                    <h2 className="text-4xl md:text-6xl font-extrabold text-[#0b0f1a] leading-[1.05] tracking-tight text-balance">
                        {title[0]} <br />
                        <span className="font-display italic font-normal">{title[1]}</span>{" "}
                        {title[2]}
                    </h2>
                    <p className="mt-6 text-[#64748b] text-lg leading-relaxed max-w-2xl">{desc}</p>
                </div>

                <div className="mt-16 grid lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
                        {probs.map((p, i) => (
                            <div
                                key={i}
                                data-testid={`problem-card-${i}`}
                                className="reveal group bg-white border border-[#e2e8f0] rounded-2xl p-6 lift relative overflow-hidden"
                                style={{ transitionDelay: `${i * 80}ms` }}
                            >
                                <div className="absolute top-0 left-0 h-full w-1 bg-gradient-to-b from-[#dc2626] to-[#f59e0b]" />
                                <div className="flex items-baseline gap-3">
                                    <span className="text-4xl sm:text-5xl font-extrabold text-[#0b0f1a] font-numbers tracking-tight">
                                        {p.n}
                                    </span>
                                    <span className="text-sm text-[#64748b] font-medium">{probContext}</span>
                                </div>
                                <div className="mt-3 text-[#0b0f1a] font-bold text-lg">{p.t}</div>
                                <div className="mt-2 text-[#64748b] text-sm leading-relaxed">{p.d}</div>
                            </div>
                        ))}
                    </div>

                    <div
                        className="reveal lg:col-span-5 relative bg-[#0b0f1a] rounded-2xl p-8 overflow-hidden"
                        style={{ transitionDelay: "240ms" }}
                        data-testid="solution-panel"
                    >
                        <div
                            className="absolute -top-32 -right-32 w-72 h-72 rounded-full opacity-40 blur-3xl"
                            style={{ background: "radial-gradient(circle, #dc2626 0%, transparent 65%)" }}
                        />
                        <div className="relative">
                            <div className="inline-flex items-center gap-2 bg-[#dc2626]/20 text-[#f87171] text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full">
                                <Sparkles className="w-3.5 h-3.5" />
                                {solKicker}
                            </div>
                            <h3 className="mt-5 text-white text-2xl md:text-3xl font-bold leading-tight">
                                {solTitle[0]}<br />{solTitle[1]}
                            </h3>
                            <ul className="mt-6 space-y-3">
                                {sols.map((s, i) => (
                                    <li key={i} className="flex items-start gap-3 text-white/80 text-[15px]">
                                        <span className="mt-1.5 block w-1.5 h-1.5 rounded-full bg-[#dc2626] shrink-0" />
                                        {s}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ======================================================
   MODULES
   ====================================================== */
const ICONS = [Radar, LineChart, Users, ShieldCheck, BrainCircuit];

function ModulePreview({ mod, Icon, index }) {
    const { t } = useT();
    const seed = (n) => {
        let s = (index + 1) * 9301 + n * 49297;
        return 0.25 + ((s % 1000) / 1000) * 0.7;
    };
    const bars = Array.from({ length: 12 }, (_, i) => seed(i));

    return (
        <div
            key={`preview-${index}`}
            className="relative window-shadow rounded-2xl overflow-hidden bg-gradient-to-br from-[#121827] via-[#0f1625] to-[#0b0f1a] border border-white/5"
            style={{ animation: "fade 0.55s ease" }}
        >
            <div
                className="absolute -top-24 -right-24 w-80 h-80 rounded-full opacity-40 blur-3xl"
                style={{ background: "radial-gradient(circle, #dc2626, transparent 70%)" }}
            />
            <div
                className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full opacity-30 blur-3xl"
                style={{ background: "radial-gradient(circle, #f59e0b, transparent 70%)" }}
            />
            <div className="absolute inset-0 bg-grid-fade opacity-20" />

            <div className="relative p-5 sm:p-8 md:p-10 module-window">
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-2xl bg-[#dc2626] flex items-center justify-center relative">
                            <Icon className="w-7 h-7 text-white" strokeWidth={1.8} />
                            <span className="absolute inset-0 rounded-2xl animate-pulse-ring" />
                        </div>
                        <div>
                            <div className="text-white/40 text-[10px] font-bold tracking-widest uppercase">
                                {t.modules.moduleLabel} {mod.num}
                            </div>
                            <div className="text-white font-bold text-lg">{mod.name}</div>
                        </div>
                    </div>
                    <div className="hidden md:flex items-center gap-2 text-white/40 text-xs font-mono">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                        LIVE · {new Date().getFullYear()}
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                    {mod.kpis.map((k, i) => (
                        <div key={i} className="relative rounded-xl bg-white/[0.04] border border-white/5 p-4 overflow-hidden">
                            <div className="text-[10px] text-white/40 font-bold uppercase tracking-wider">{k.l}</div>
                            <div className="mt-2 text-3xl sm:text-4xl md:text-5xl font-extrabold text-white font-numbers tracking-tight">
                                {k.v}
                            </div>
                            <div
                                className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-[#dc2626] to-[#f59e0b]"
                                style={{ width: `${50 + i * 20}%` }}
                            />
                        </div>
                    ))}
                </div>

                <div className="mt-6 rounded-xl bg-white/[0.03] border border-white/5 p-5">
                    <div className="flex items-center justify-between mb-4">
                        <div className="text-white/60 text-xs font-bold uppercase tracking-wider">
                            {t.modules.chartTitle}
                        </div>
                        <div className="flex items-center gap-4 text-[10px] font-mono text-white/40">
                            <span className="flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-sm bg-[#dc2626]" />
                                {t.modules.chartActual}
                            </span>
                            <span className="flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-sm bg-white/20" />
                                {t.modules.chartTarget}
                            </span>
                        </div>
                    </div>
                    <div className="flex items-end gap-1.5 h-32">
                        {bars.map((h, i) => {
                            const hot = h > 0.7;
                            return (
                                <div
                                    key={i}
                                    className="flex-1 rounded-sm transition-all duration-500"
                                    style={{
                                        height: `${h * 100}%`,
                                        background: hot
                                            ? "linear-gradient(180deg, #f87171, #dc2626)"
                                            : "linear-gradient(180deg, #dc2626, #7f1d1d)",
                                        opacity: 0.5 + h * 0.5,
                                    }}
                                />
                            );
                        })}
                    </div>
                    <div className="mt-3 flex items-center justify-between text-[10px] font-mono text-white/30">
                        {t.modules.months.map((m, i) => <span key={i}>{m}</span>)}
                    </div>
                </div>

                <div className="mt-5 grid grid-cols-3 gap-3">
                    <StatusTile Icon={Activity} color="text-green-400" label={t.modules.statusLabels.uptime} value="99.9%" />
                    <StatusTile Icon={Fingerprint} color="text-[#f87171]" label={t.modules.statusLabels.security} value="AES-256" />
                    <StatusTile Icon={Zap} color="text-[#f59e0b]" label={t.modules.statusLabels.response} value="< 1 s" />
                </div>
            </div>
        </div>
    );
}

function StatusTile({ Icon, color, label, value }) {
    return (
        <div className="flex items-center gap-2.5 rounded-lg bg-white/[0.03] border border-white/5 px-3 py-2.5">
            <Icon className={`w-4 h-4 ${color}`} />
            <div>
                <div className="text-[10px] text-white/40 uppercase tracking-wider">{label}</div>
                <div className="text-white text-sm font-bold font-mono">{value}</div>
            </div>
        </div>
    );
}

function ModulesSection() {
    const { t } = useT();
    const [active, setActive] = useState(0);
    const modules = t.modules.items;
    const mod = modules[active];
    const Icon = ICONS[active];

    return (
        <section
            id="moduller"
            data-testid="moduller-section"
            className="relative py-28 md:py-36 bg-[#0b0f1a] overflow-hidden"
        >
            <div
                className="absolute top-1/3 -left-40 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl animate-blob-2"
                style={{ background: "radial-gradient(circle, #dc2626 0%, transparent 60%)" }}
            />
            <div className="absolute inset-0 bg-grid-fade opacity-30" />

            <div className="relative max-w-7xl mx-auto px-5 md:px-8">
                <div className="max-w-3xl reveal">
                    <div className="text-[#f87171] text-sm font-bold tracking-widest uppercase mb-3">
                        {t.modules.kicker}
                    </div>
                    <h2 className="text-4xl md:text-6xl font-extrabold text-white leading-[1.05] tracking-tight text-balance">
                        {t.modules.title[0]}{" "}
                        <span className="font-display italic font-normal text-white/70">{t.modules.title[1]}</span>{" "}
                        {t.modules.title[2]}
                    </h2>
                    <p className="mt-6 text-white/55 text-lg leading-relaxed max-w-2xl">{t.modules.desc}</p>
                </div>

                <div className="mt-14 -mx-5 md:mx-0 px-5 md:px-0 overflow-x-auto scroll-x-snap reveal" data-testid="modules-tabs">
                    <div className="flex gap-2 md:flex-wrap w-max md:w-auto pb-2 md:pb-0">
                    {modules.map((m, i) => {
                        const Ic = ICONS[i];
                        const isActive = i === active;
                        return (
                            <button
                                key={m.id}
                                onClick={() => setActive(i)}
                                data-testid={`module-tab-${m.id}`}
                                className={`group flex items-center gap-2.5 px-4 sm:px-5 py-2.5 sm:py-3 rounded-full text-xs sm:text-sm font-semibold transition-all shrink-0 ${
                                    isActive
                                        ? "bg-[#dc2626] text-white tab-active-ring"
                                        : "bg-white/5 text-white/70 hover:bg-white/10 border border-white/10"
                                }`}
                            >
                                <span className={`font-mono text-xs opacity-70 ${isActive ? "text-white" : ""}`}>
                                    {m.num}
                                </span>
                                <Ic className="w-4 h-4" />
                                {m.name}
                            </button>
                        );
                    })}
                    </div>
                </div>

                <div className="mt-10 grid lg:grid-cols-12 gap-8 items-start">
                    <div className="lg:col-span-5 reveal in" key={`left-${active}`} data-testid="module-detail">
                        <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-1 text-xs font-semibold text-white/70 uppercase tracking-widest">
                            <Icon className="w-3.5 h-3.5 text-[#f87171]" />
                            {t.modules.moduleLabel} {mod.num}
                        </div>
                        <h3 className="mt-5 text-white text-3xl md:text-4xl font-bold leading-tight">{mod.title}</h3>
                        <p className="mt-4 text-white/60 text-base leading-relaxed">{mod.tagline}</p>

                        <div className="mt-6 grid grid-cols-3 gap-3">
                            {mod.kpis.map((k, i) => (
                                <div key={i} className="glass rounded-xl px-3 py-4 text-center">
                                    <div className="text-xl sm:text-2xl md:text-3xl font-extrabold text-[#f87171] font-numbers">
                                        {k.v}
                                    </div>
                                    <div className="text-[10px] md:text-xs text-white/50 uppercase mt-1 font-medium tracking-wider">
                                        {k.l}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <ul className="mt-7 space-y-2.5">
                            {mod.bullets.map((b, i) => (
                                <li key={i} className="flex items-start gap-3 text-white/75 text-sm leading-relaxed">
                                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#dc2626] shrink-0" />
                                    {b}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="lg:col-span-7 reveal" data-testid="module-preview">
                        <ModulePreview mod={mod} Icon={Icon} index={active} />
                        <div className="mt-4 flex items-center justify-between text-white/40 text-xs font-mono">
                            <span>
                                {String(active + 1).padStart(2, "0")} /{" "}
                                {String(modules.length).padStart(2, "0")}
                            </span>
                            <span className="flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                                {t.modules.livePreview}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ======================================================
   FEATURES
   ====================================================== */
const FEATURE_ICONS = [Bot, Workflow, Gauge, Radar, TrendingUp, Sparkles];

function FeaturesSection() {
    const { t } = useT();
    return (
        <section data-testid="features-section" className="relative py-28 md:py-36 bg-[#f8fafc]">
            <div className="max-w-7xl mx-auto px-5 md:px-8">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 reveal">
                    <div className="max-w-2xl">
                        <div className="text-[#dc2626] text-sm font-bold tracking-widest uppercase mb-3">
                            {t.features.kicker}
                        </div>
                        <h2 className="text-4xl md:text-6xl font-extrabold text-[#0b0f1a] leading-[1.05] tracking-tight text-balance">
                            {t.features.title[0]}{" "}
                            <span className="font-display italic font-normal">{t.features.title[1]}</span>
                        </h2>
                    </div>
                    <p className="text-[#64748b] text-base md:text-right md:max-w-md">{t.features.desc}</p>
                </div>

                <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {t.features.items.map((f, i) => {
                        const Ic = FEATURE_ICONS[i];
                        return (
                            <div
                                key={i}
                                data-testid={`feature-card-${i}`}
                                className="reveal group relative bg-white border border-[#e2e8f0] rounded-2xl p-7 lift overflow-hidden"
                                style={{ transitionDelay: `${i * 70}ms` }}
                            >
                                <div
                                    className="absolute -top-20 -right-20 w-48 h-48 rounded-full opacity-0 group-hover:opacity-60 transition-opacity blur-2xl"
                                    style={{ background: "radial-gradient(circle, rgba(220,38,38,0.25), transparent 70%)" }}
                                />
                                <div className="relative">
                                    <div className="w-12 h-12 rounded-xl bg-[#0b0f1a] flex items-center justify-center mb-5 group-hover:bg-[#dc2626] transition-colors">
                                        <Ic className="w-5 h-5 text-white" strokeWidth={1.8} />
                                    </div>
                                    <h3 className="text-[#0b0f1a] text-xl font-bold">{f.title}</h3>
                                    <p className="mt-2.5 text-[#64748b] text-sm leading-relaxed">{f.desc}</p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

/* ======================================================
   ARCHITECTURE
   ====================================================== */
const ARCH_ICONS = [Database, BrainCircuit, BarChart3];

function ArchitectureSection() {
    const { t } = useT();
    return (
        <section id="teknoloji" data-testid="teknoloji-section" className="relative py-28 md:py-36 bg-[#0b0f1a] overflow-hidden">
            <div
                className="absolute inset-0 opacity-30"
                style={{ background: "radial-gradient(1200px circle at 50% 0%, rgba(220,38,38,0.18), transparent 50%)" }}
            />
            <div className="absolute inset-0 bg-grid-fade opacity-30" />

            <div className="relative max-w-7xl mx-auto px-5 md:px-8">
                <div className="max-w-3xl reveal">
                    <div className="text-[#f87171] text-sm font-bold tracking-widest uppercase mb-3">
                        {t.arch.kicker}
                    </div>
                    <h2 className="text-4xl md:text-6xl font-extrabold text-white leading-[1.05] tracking-tight text-balance">
                        {t.arch.title[0]}{" "}
                        <span className="font-display italic font-normal text-white/70">{t.arch.title[1]}</span>{" "}
                        {t.arch.title[2]}
                    </h2>
                    <p className="mt-6 text-white/55 text-lg leading-relaxed max-w-2xl">{t.arch.desc}</p>
                </div>

                <div className="mt-14 grid md:grid-cols-3 gap-5 relative">
                    <div className="hidden md:block absolute top-1/2 left-[33%] right-[33%] h-[2px] bg-gradient-to-r from-[#dc2626] via-[#f59e0b] to-[#dc2626] opacity-40 -translate-y-1/2" />
                    {t.arch.cols.map((c, i) => {
                        const Ic = ARCH_ICONS[i];
                        return (
                            <div
                                key={i}
                                className="reveal glass rounded-2xl p-7 relative"
                                style={{ transitionDelay: `${i * 100}ms` }}
                                data-testid={`arch-column-${i}`}
                            >
                                <div className="flex items-center justify-between mb-5">
                                    <div className="w-11 h-11 rounded-lg bg-[#dc2626] flex items-center justify-center">
                                        <Ic className="w-5 h-5 text-white" strokeWidth={2} />
                                    </div>
                                    <span className="font-mono text-xs text-white/40">0{i + 1}</span>
                                </div>
                                <h3 className="text-white text-xl font-bold">{c.title}</h3>
                                <ul className="mt-4 space-y-2">
                                    {c.items.map((it, j) => (
                                        <li key={j} className="flex items-center gap-2 text-white/65 text-sm">
                                            <span className="w-1 h-1 rounded-full bg-[#dc2626]" />
                                            {it}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

/* ======================================================
   STATS
   ====================================================== */
function StatsSection() {
    const { t } = useT();
    return (
        <section className="relative py-24 bg-[#f8fafc]" data-testid="stats-section">
            <div className="max-w-7xl mx-auto px-5 md:px-8">
                <div className="grid md:grid-cols-4 gap-8 text-center">
                    {t.stats.map((s, i) => (
                        <div key={i} className="reveal" style={{ transitionDelay: `${i * 80}ms` }}>
                            <div className="text-6xl md:text-7xl lg:text-8xl font-extrabold text-gradient-red font-numbers tracking-tight">
                                {s.v}
                            </div>
                            <div className="mt-3 text-[#0b0f1a] text-lg font-bold">{s.l}</div>
                            <div className="mt-1 text-[#64748b] text-sm">{s.d}</div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ======================================================
   BLOG / INSIGHTS
   ====================================================== */
function BlogSection() {
    const { lang } = useT();
    const b = BLOG[lang];
    const slug = BLOG.slug[lang];
    const postPath = lang === "en" ? `/en/blog/${slug}` : `/blog/${slug}`;
    const readLabel = lang === "en" ? "Read full article" : "Yazının tamamı";
    return (
        <section
            id="blog"
            data-testid="blog-section"
            className="relative py-28 md:py-36 bg-[#f8fafc] overflow-hidden"
        >
            {/* soft decorative accents */}
            <div
                className="absolute -top-40 -right-20 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl"
                style={{ background: "radial-gradient(circle, #dc2626, transparent 70%)" }}
            />
            <div className="relative max-w-7xl mx-auto px-5 md:px-8">
                <div className="max-w-3xl reveal">
                    <div className="text-[#dc2626] text-sm font-bold tracking-widest uppercase mb-3">
                        {b.sectionKicker}
                    </div>
                    <h2 className="text-4xl md:text-6xl font-extrabold text-[#0b0f1a] leading-[1.05] tracking-tight text-balance">
                        {b.sectionTitle[0]}{" "}
                        <span className="font-display italic font-normal">
                            {b.sectionTitle[1]}
                        </span>{" "}
                        {b.sectionTitle[2]}
                    </h2>
                    <p className="mt-6 text-[#64748b] text-lg leading-relaxed max-w-2xl">
                        {b.sectionDesc}
                    </p>
                </div>

                {/* Article card */}
                <article
                    className="reveal mt-14 max-w-4xl mx-auto bg-white rounded-3xl border border-[#e2e8f0] overflow-hidden"
                    data-testid="blog-article"
                    style={{ boxShadow: "0 40px 80px -30px rgba(11,15,26,0.25)" }}
                >
                    {/* decorative article header */}
                    <div className="relative bg-[#0b0f1a] px-6 sm:px-10 md:px-14 py-10 sm:py-12 md:py-14 overflow-hidden">
                        <div
                            className="absolute -top-20 -right-20 w-72 h-72 rounded-full opacity-30 blur-3xl"
                            style={{
                                background:
                                    "radial-gradient(circle, #dc2626, transparent 70%)",
                            }}
                        />
                        <div
                            className="absolute -bottom-16 -left-10 w-56 h-56 rounded-full opacity-20 blur-3xl"
                            style={{
                                background:
                                    "radial-gradient(circle, #f59e0b, transparent 70%)",
                            }}
                        />
                        <div className="absolute inset-0 bg-grid-fade opacity-25" />
                        <div className="relative">
                            <div className="flex flex-wrap items-center gap-3 text-xs font-bold tracking-widest uppercase text-white/60">
                                <span className="text-[#f87171]">{b.category}</span>
                                <span className="w-1 h-1 rounded-full bg-white/30" />
                                <span>{b.date}</span>
                                <span className="w-1 h-1 rounded-full bg-white/30" />
                                <span>{b.readTime}</span>
                            </div>
                            <h3
                                data-testid="blog-title"
                                className="mt-5 text-white text-2xl sm:text-3xl md:text-5xl font-extrabold leading-[1.08] tracking-tight text-balance"
                            >
                                {b.title}
                            </h3>
                            <p className="mt-5 sm:mt-6 text-white/70 text-base sm:text-lg md:text-xl font-light leading-relaxed max-w-3xl font-display italic">
                                {b.lede}
                            </p>
                            <div className="mt-8 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#dc2626] to-[#7f1d1d] flex items-center justify-center">
                                    <span className="text-white text-sm font-bold">PD</span>
                                </div>
                                <div>
                                    <div className="text-white text-sm font-bold">
                                        {b.author.name}
                                    </div>
                                    <div className="text-white/50 text-xs">
                                        {b.author.role}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* body preview */}
                    <div className="px-5 sm:px-8 md:px-14 py-10 md:py-14">
                        {/* Show only first paragraph as preview */}
                        {b.sections
                            .filter((s) => s.type === "p")
                            .slice(0, 1)
                            .map((s, i) => (
                                <BlogBlock key={i} block={s} first />
                            ))}
                        <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4">
                            <Link
                                to={postPath}
                                data-testid="blog-read-full"
                                className="group inline-flex items-center justify-center gap-2 bg-[#0b0f1a] hover:bg-[#dc2626] text-white font-semibold px-6 py-3.5 rounded-full shine transition-colors w-fit"
                            >
                                {readLabel}
                                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                            </Link>
                            <span className="text-[#64748b] text-xs sm:text-sm break-all sm:break-normal">
                                {lang === "en"
                                    ? "Permalink: "
                                    : "Kalıcı bağlantı: "}
                                <span className="font-mono text-[#0b0f1a]">
                                    {postPath}
                                </span>
                            </span>
                        </div>
                    </div>
                </article>
            </div>
        </section>
    );
}

function BlogBlock({ block, first }) {
    if (block.type === "h3") {
        return (
            <h4 className="mt-12 mb-4 text-[#0b0f1a] text-2xl md:text-3xl font-bold tracking-tight flex items-baseline gap-3">
                <span className="w-8 h-[2px] bg-[#dc2626]" />
                {block.text}
            </h4>
        );
    }
    if (block.type === "quote") {
        return (
            <figure className="my-10 md:my-12 border-l-4 border-[#dc2626] pl-6 md:pl-8">
                <blockquote className="text-[#0b0f1a] text-xl md:text-2xl font-display italic leading-snug text-balance">
                    "{block.text}"
                </blockquote>
                {block.cite && (
                    <figcaption className="mt-4 text-[#dc2626] text-xs font-bold tracking-widest uppercase">
                        — {block.cite}
                    </figcaption>
                )}
            </figure>
        );
    }
    if (block.type === "list") {
        return (
            <ul className="my-6 space-y-3">
                {block.items.map((it, i) => (
                    <li
                        key={i}
                        className="flex items-start gap-3 text-[#0b0f1a] text-base md:text-[17px] leading-relaxed"
                    >
                        <span
                            className="mt-2 w-2 h-2 rounded-sm shrink-0"
                            style={{
                                background:
                                    "linear-gradient(135deg, #dc2626 0%, #f59e0b 100%)",
                            }}
                        />
                        <span>{it}</span>
                    </li>
                ))}
            </ul>
        );
    }
    if (block.type === "cta") {
        return (
            <div className="mt-12 md:mt-16 relative overflow-hidden rounded-2xl bg-[#0b0f1a] p-8 md:p-10">
                <div
                    className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-40 blur-3xl"
                    style={{
                        background:
                            "radial-gradient(circle, #dc2626, transparent 70%)",
                    }}
                />
                <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                    <div>
                        <div className="text-white text-2xl md:text-3xl font-bold tracking-tight">
                            {block.title}
                        </div>
                        <div className="mt-2 text-white/60 text-sm md:text-base max-w-xl">
                            {block.desc}
                        </div>
                    </div>
                    <a
                        href="mailto:selim@pdtech.com.tr?subject=Virtual%20CFO%20Demo"
                        data-testid="blog-cta"
                        className="shrink-0 inline-flex items-center gap-2 bg-[#dc2626] hover:bg-[#b91c1c] text-white font-semibold px-6 py-3.5 rounded-full shine transition-colors"
                    >
                        {block.button}
                        <ArrowRight className="w-4 h-4" />
                    </a>
                </div>
            </div>
        );
    }
    // paragraph
    if (first) {
        // drop cap on the first paragraph
        const first_char = block.text.charAt(0);
        const rest = block.text.slice(1);
        return (
            <p className="text-[#0b0f1a] text-lg md:text-[19px] leading-[1.75] font-light">
                <span className="blog-dropcap float-left font-display not-italic text-[72px] leading-[0.9] mr-2 mt-1 text-[#dc2626]">
                    {first_char}
                </span>
                {rest}
            </p>
        );
    }
    return (
        <p className="mt-5 text-[#0b0f1a]/90 text-base md:text-[17px] leading-[1.8] font-light">
            {block.text}
        </p>
    );
}

/* ======================================================
   ROADMAP
   ====================================================== */
const PHASE_ICONS = [Compass, Rocket, Workflow, Zap];

function RoadmapSection() {
    const { t } = useT();
    return (
        <section className="relative py-28 md:py-32 bg-white border-t border-[#e2e8f0]" data-testid="roadmap-section">
            <div className="max-w-7xl mx-auto px-5 md:px-8">
                <div className="max-w-3xl reveal">
                    <div className="text-[#dc2626] text-sm font-bold tracking-widest uppercase mb-3">
                        {t.roadmap.kicker}
                    </div>
                    <h2 className="text-4xl md:text-5xl font-extrabold text-[#0b0f1a] leading-[1.05] tracking-tight">
                        {t.roadmap.title[0]}{" "}
                        <span className="font-display italic font-normal">{t.roadmap.title[1]}</span>
                    </h2>
                </div>

                <div className="mt-14 grid md:grid-cols-4 gap-5 relative">
                    {t.roadmap.phases.map((p, i) => {
                        const Ic = PHASE_ICONS[i];
                        return (
                            <div
                                key={i}
                                className="reveal relative bg-white border border-[#e2e8f0] rounded-2xl p-7 lift"
                                style={{ transitionDelay: `${i * 80}ms` }}
                                data-testid={`roadmap-phase-${i}`}
                            >
                                <div className="flex items-center justify-between mb-5">
                                    <div className="w-12 h-12 rounded-full bg-[#dc2626] text-white flex items-center justify-center font-bold text-lg">
                                        {i + 1}
                                    </div>
                                    <Ic className="w-5 h-5 text-[#dc2626]" />
                                </div>
                                <h3 className="text-[#0b0f1a] text-xl font-bold">{p.t}</h3>
                                <div className="mt-1 text-xs font-bold text-[#dc2626] uppercase tracking-wider">
                                    {p.dur}
                                </div>
                                <div className="mt-3 w-10 h-0.5 bg-[#dc2626]" />
                                <p className="mt-3 text-[#64748b] text-sm leading-relaxed">{p.d}</p>
                            </div>
                        );
                    })}
                </div>

                <p className="mt-10 text-center text-[#64748b] italic reveal">{t.roadmap.note}</p>
            </div>
        </section>
    );
}

/* ======================================================
   CONTACT
   ====================================================== */
const ACTION_ICONS = [Rocket, MapIcon, Workflow];

function ContactSection() {
    const { t } = useT();
    return (
        <section id="iletisim" data-testid="iletisim-section" className="relative py-32 md:py-40 bg-[#0b0f1a] overflow-hidden">
            <div
                className="absolute inset-0 opacity-50"
                style={{ background: "radial-gradient(900px circle at 50% 50%, rgba(220,38,38,0.28), transparent 55%)" }}
            />
            <div
                className="absolute -top-20 left-10 w-80 h-80 rounded-full opacity-30 blur-3xl animate-blob-1"
                style={{ background: "radial-gradient(circle, #f59e0b, transparent 70%)" }}
            />
            <div className="absolute inset-0 bg-grid-fade opacity-20" />

            <div className="relative max-w-5xl mx-auto px-5 md:px-8 text-center">
                <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 text-white/70 text-xs font-semibold uppercase tracking-widest mb-8 reveal">
                    <Sparkles className="w-3.5 h-3.5 text-[#f87171]" />
                    {t.contact.badge}
                </div>
                <h2
                    data-testid="contact-title"
                    className="reveal text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.05] tracking-tight text-balance"
                >
                    {t.contact.title[0]}{" "}
                    <span className="font-display italic font-normal text-white/80">{t.contact.title[1]}</span>{" "}
                    {t.contact.title[2]}
                </h2>
                <p className="reveal mt-6 text-white/55 text-lg max-w-2xl mx-auto" style={{ transitionDelay: "100ms" }}>
                    {t.contact.desc}
                </p>

                <div className="reveal mt-12 flex flex-col sm:flex-row gap-4 justify-center items-center" style={{ transitionDelay: "200ms" }}>
                    <a
                        href="mailto:selim@pdtech.com.tr?subject=Virtual%20CFO%20Demo"
                        data-testid="contact-email-cta"
                        className="group inline-flex items-center gap-3 bg-[#dc2626] hover:bg-[#b91c1c] text-white font-semibold px-8 py-4 rounded-full shine transition-colors"
                    >
                        <Mail className="w-5 h-5" />
                        selim@pdtech.com.tr
                        <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                    </a>
                    <a
                        href="#moduller"
                        data-testid="contact-modules-cta"
                        className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/15 text-white font-semibold px-7 py-4 rounded-full transition-colors"
                    >
                        {t.contact.cta2}
                    </a>
                </div>

                <div
                    className="reveal mt-14 grid grid-cols-1 md:grid-cols-3 gap-4 text-left max-w-3xl mx-auto"
                    style={{ transitionDelay: "280ms" }}
                >
                    {t.contact.actions.map((a, i) => {
                        const Ic = ACTION_ICONS[i];
                        return (
                            <div key={i} className="glass rounded-2xl p-5 lift" data-testid={`contact-action-${i}`}>
                                <Ic className="w-5 h-5 text-[#f87171] mb-3" />
                                <div className="text-white font-bold">{a.t}</div>
                                <div className="text-white/55 text-sm mt-1">{a.d}</div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

/* ======================================================
   FOOTER
   ====================================================== */
function Footer() {
    const { t } = useT();
    return (
        <footer data-testid="site-footer" className="bg-[#050811] border-t border-white/5 pt-16 pb-10">
            <div className="max-w-7xl mx-auto px-5 md:px-8">
                <div className="grid md:grid-cols-12 gap-10">
                    <div className="md:col-span-5">
                        <div className="bg-white rounded-md inline-flex px-3 py-2">
                            <img src={LOGO} alt="PDTECH" className="h-6" />
                        </div>
                        <p className="mt-5 text-white/50 text-sm leading-relaxed max-w-sm">{t.footer.tagline}</p>
                        <div className="mt-5">
                            <LangToggle dark />
                        </div>
                    </div>

                    <div className="md:col-span-4">
                        <div className="text-white/40 text-xs font-bold uppercase tracking-widest mb-4">
                            {t.footer.platform}
                        </div>
                        <ul className="space-y-2">
                            {t.modules.items.map((m) => (
                                <li key={m.id}>
                                    <a href="#moduller" className="text-white/75 hover:text-white text-sm link-underline">
                                        {m.name}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="md:col-span-3">
                        <div className="text-white/40 text-xs font-bold uppercase tracking-widest mb-4">
                            {t.footer.contact}
                        </div>
                        <a
                            href="mailto:selim@pdtech.com.tr"
                            data-testid="footer-email"
                            className="flex items-center gap-2 text-white/85 hover:text-[#f87171] text-sm transition-colors"
                        >
                            <Mail className="w-4 h-4" />
                            selim@pdtech.com.tr
                        </a>
                    </div>
                </div>

                <div className="mt-14 pt-6 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="text-white/40 text-xs font-medium text-center md:text-left">
                        © {new Date().getFullYear()} {t.footer.company} {t.footer.rights}
                    </div>
                    <div className="flex items-center gap-2 text-white/30 text-[11px] font-mono uppercase tracking-widest">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#dc2626] animate-pulse" />
                        Virtual CFO Platform · v1.0
                    </div>
                </div>
            </div>
        </footer>
    );
}

/* ======================================================
   PAGE
   ====================================================== */
function Page() {
    useReveal();
    useHideBadge();
    const { lang } = useT();
    // re-trigger reveals on language change so new text animates in
    useEffect(() => {
        document.querySelectorAll(".reveal").forEach((el) => el.classList.add("in"));
    }, [lang]);
    const landingTitle = lang === "en"
        ? "Virtual CFO — AI-Powered Financial Intelligence Platform | PDTECH"
        : "Virtual CFO — Yapay Zeka Destekli Finansal Zeka Platformu | PDTECH";
    const landingDesc = lang === "en"
        ? "Virtual CFO unifies dealer performance, credit risk, price elasticity and financial intelligence into a single AI platform for the CFO office."
        : "Virtual CFO, bayi performansından kredi riskine, fiyat elastikiyetinden finansal istihbarata kadar CFO ofisinin tüm operasyonunu tek AI platformunda birleştirir.";
    return (
        <div className="App bg-white">
            <SeoHead
                title={landingTitle}
                description={landingDesc}
                path={lang === "en" ? "/en" : "/"}
                trPath="/"
                enPath="/en"
            />
            <Navbar />
            <Hero />
            <WhySection />
            <ModulesSection />
            <FeaturesSection />
            <ArchitectureSection />
            <StatsSection />
            <BlogSection />
            <RoadmapSection />
            <ContactSection />
            <Footer />
        </div>
    );
}

/* ======================================================
   READING PROGRESS BAR (for blog post pages)
   ====================================================== */
function ReadingProgress({ target = "article-body" }) {
    const [pct, setPct] = useState(0);
    useEffect(() => {
        const onScroll = () => {
            const el = document.getElementById(target);
            if (!el) {
                setPct(0);
                return;
            }
            const rect = el.getBoundingClientRect();
            const total = el.offsetHeight - window.innerHeight;
            const scrolled = Math.min(
                Math.max(-rect.top, 0),
                Math.max(total, 0)
            );
            const p = total > 0 ? (scrolled / total) * 100 : 0;
            setPct(Math.min(100, Math.max(0, p)));
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);
        return () => {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
        };
    }, [target]);
    return (
        <div
            aria-hidden="true"
            data-testid="reading-progress"
            className="fixed top-0 left-0 right-0 z-[60] h-[3px] pointer-events-none"
        >
            <div
                className="h-full bg-gradient-to-r from-[#dc2626] via-[#f59e0b] to-[#dc2626]"
                style={{
                    width: `${pct}%`,
                    transition: "width 80ms linear",
                    boxShadow:
                        pct > 0 ? "0 0 12px rgba(220, 38, 38, 0.6)" : "none",
                }}
            />
        </div>
    );
}

/* ======================================================
   BLOG POST PAGE (standalone URL)
   ====================================================== */
function BlogPostPage() {
    useReveal();
    useHideBadge();
    const { lang } = useT();
    const { slug } = useParams();
    const expected = BLOG.slug[lang];

    // Scroll to top on page change (hook MUST be called unconditionally)
    useEffect(() => {
        window.scrollTo({ top: 0 });
    }, [slug, lang]);

    // Validate slug matches; if user lands on /blog/wrong-slug, redirect
    if (slug !== expected) {
        return <Navigate to={lang === "en" ? `/en/blog/${BLOG.slug.en}` : `/blog/${BLOG.slug.tr}`} replace />;
    }

    const b = BLOG[lang];
    const path = lang === "en" ? `/en/blog/${BLOG.slug.en}` : `/blog/${BLOG.slug.tr}`;
    const trPath = `/blog/${BLOG.slug.tr}`;
    const enPath = `/en/blog/${BLOG.slug.en}`;

    const seoTitle = `${b.title} | PDTECH Virtual CFO`;

    // JSON-LD Article schema
    const articleJsonLd = {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: b.title,
        description: b.lede,
        author: { "@type": "Organization", name: "PDTECH" },
        publisher: {
            "@type": "Organization",
            name: "PDTECH",
            logo: { "@type": "ImageObject", url: `${SITE_ORIGIN}/assets/logo/pdtech.png` },
        },
        datePublished: "2026-04-21",
        inLanguage: lang === "en" ? "en" : "tr",
        mainEntityOfPage: SITE_ORIGIN + path,
    };

    return (
        <div className="App bg-white">
            <ReadingProgress target="article-body" />
            <SeoHead
                title={seoTitle}
                description={b.lede}
                path={path}
                trPath={trPath}
                enPath={enPath}
                type="article"
            />
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(articleJsonLd)}
                </script>
            </Helmet>

            <Navbar />

            {/* Article hero */}
            <section className="relative pt-36 pb-20 bg-[#0b0f1a] overflow-hidden" data-testid="blog-post-hero">
                <div
                    className="absolute -top-40 -right-40 w-[700px] h-[700px] rounded-full opacity-25 blur-3xl animate-blob-1"
                    style={{ background: "radial-gradient(circle at 30% 30%, #dc2626 0%, transparent 60%)" }}
                />
                <div
                    className="absolute -bottom-60 -left-40 w-[600px] h-[600px] rounded-full opacity-20 blur-3xl animate-blob-2"
                    style={{ background: "radial-gradient(circle at 70% 70%, #f59e0b 0%, transparent 55%)" }}
                />
                <div className="absolute inset-0 bg-grid-fade opacity-30" />

                <div className="relative max-w-4xl mx-auto px-5 md:px-8">
                    <Link
                        to={lang === "en" ? "/en" : "/"}
                        data-testid="blog-back-home"
                        className="inline-flex items-center gap-2 text-white/60 hover:text-[#f87171] text-sm font-medium mb-8 transition-colors"
                    >
                        <ChevronRight className="w-4 h-4 rotate-180" />
                        {lang === "en" ? "Back to platform" : "Platforma dön"}
                    </Link>

                    <div className="flex flex-wrap items-center gap-3 text-xs font-bold tracking-widest uppercase text-white/60">
                        <span className="text-[#f87171]">{b.category}</span>
                        <span className="w-1 h-1 rounded-full bg-white/30" />
                        <span>{b.date}</span>
                        <span className="w-1 h-1 rounded-full bg-white/30" />
                        <span>{b.readTime}</span>
                    </div>
                    <h1
                        data-testid="blog-post-title"
                        className="reveal in mt-5 text-white text-3xl sm:text-4xl md:text-6xl font-extrabold leading-[1.05] tracking-tight text-balance"
                    >
                        {b.title}
                    </h1>
                    <p className="mt-6 text-white/70 text-lg md:text-2xl font-light leading-relaxed font-display italic">
                        {b.lede}
                    </p>
                    <div className="mt-8 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#dc2626] to-[#7f1d1d] flex items-center justify-center">
                            <span className="text-white text-sm font-bold">PD</span>
                        </div>
                        <div>
                            <div className="text-white text-sm font-bold">{b.author.name}</div>
                            <div className="text-white/50 text-xs">{b.author.role}</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Article body */}
            <section id="article-body" className="bg-white py-16 md:py-24">
                <div className="max-w-3xl mx-auto px-5 md:px-8">
                    {b.sections.map((s, i) => (
                        <BlogBlock key={i} block={s} first={i === 0} />
                    ))}
                </div>
            </section>

            <ContactSection />
            <Footer />
        </div>
    );
}

export default function App() {
    return (
        <HelmetProvider>
            <BrowserRouter>
                <LangProvider>
                    <Routes>
                        <Route path="/" element={<Page />} />
                        <Route path="/en" element={<Page />} />
                        <Route path="/blog/:slug" element={<BlogPostPage />} />
                        <Route path="/en/blog/:slug" element={<BlogPostPage />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </LangProvider>
            </BrowserRouter>
        </HelmetProvider>
    );
}
